import type { ReactNode } from 'react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'

export interface ComingSoonDialogProps {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  title: string
  description: string
  featureList: string[]
  icon?: ReactNode
  className?: string
}

/**
 * An informational dialog showing upcoming feature preview.
 * Displays a title, description, and list of planned features.
 */
export const ComingSoonDialog = ({
  isOpen,
  onOpenChange,
  title,
  description,
  featureList,
  icon,
  className,
}: ComingSoonDialogProps): ReactNode => {
  const handleClose = (): void => {
    onOpenChange(false)
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className={cn('coming-soon-dialog sm:max-w-md', className)}>
        <DialogHeader>
          <DialogTitle className="coming-soon-dialog-title flex items-center gap-2">
            {icon}
            {title}
          </DialogTitle>
          <DialogDescription className="coming-soon-dialog-description">
            {description}
          </DialogDescription>
        </DialogHeader>
        <ul className="coming-soon-dialog-features space-y-2 py-2">
          {featureList.map((feature) => (
            <li key={feature} className="flex items-start gap-2 text-sm text-muted-foreground">
              <span className="text-amber-500 mt-0.5">•</span>
              <span>{feature}</span>
            </li>
          ))}
        </ul>
        <DialogFooter>
          <Button onClick={handleClose} className="coming-soon-dialog-confirm">
            Got it
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

ComingSoonDialog.displayName = 'ComingSoonDialog'
